import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  COURSE_LANGUAGES,
  courseLanguageLabel,
  controlSrc,
  detectPlaybackProvider,
  formatClock,
  isAllowedEmbedHost,
  parseDurationInput,
  resolveRpEmbed,
} from '@mlm/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Progress,
  RichTextEditor,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  VideoPlayer,
  toast,
} from '@mlm/ui';
import {
  apiErrorPayload,
  assignCourseTemplate,
  getCreatorMe,
  getVideoUploadStatus,
  getUploadStatus,
  openLessonPreview,
  promoVideoBase,
  uploadPromoVideo,
  setPromoEmbed,
  deletePromo,
  type LessonPreview,
  cancelLessonVideoUpload,
  UploadCancelled,
  listCourseCategories,
  listCourseTags,
  listTemplatePicker,
  clearLessonVideo,
  createChapter,
  deleteLessonCaption,
  uploadLessonCaption,
  createLesson,
  deleteChapter,
  deleteLesson,
  getCourse,
  linkCourseTranslation,
  listMyCourses,
  productImageUrl,
  removeCourseCover,
  reorderChapters,
  reorderLessons,
  updateChapter,
  updateCourse,
  updateLesson,
  uploadCourseCover,
  uploadLessonVideo,
  unlinkCourseTranslation,
  type Chapter,
  type CourseDetail,
  type Lesson,
} from '../lib/api';
import { LessonAttachmentsPanel } from '../components/lesson-attachments-panel';
import { formatMb } from '../lib/upload-plan';
import { CourseTestsTab } from '../components/course-tests-tab';
import { CourseAnnouncementsTab } from '../components/course-announcements-tab';
import { listCourseTests } from '../lib/api';

/**
 * Course editor — Details (sellable + curriculum meta) / Curriculum (chapters +
 * lessons, embed URL or R2 upload per lesson) / Cover / Publish. A sellable
 * edit (title/price/…) sends the listing back to admin review; the page says
 * so before the creator saves.
 */
export function CourseEditorPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data: course } = useQuery({
    queryKey: ['creator-course', id],
    queryFn: () => getCourse(id),
  });
  const { data: rows = [] } = useQuery({ queryKey: ['creator-courses'], queryFn: listMyCourses });
  const { data: me } = useQuery({ queryKey: ['creator-me'], queryFn: getCreatorMe });
  const isHouse = me?.creator.isHouse === true;
  const row = rows.find((r) => r.id === id);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['creator-course', id] });
    qc.invalidateQueries({ queryKey: ['creator-courses'] });
  };

  if (!course) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="max-w-5xl space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/courses" className="text-xs text-muted-foreground hover:underline">
            ← Courses
          </Link>
          <h1 className="text-2xl font-semibold">{course.productName}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm">
            <Badge variant={course.status === 'published' ? 'default' : 'outline'}>
              {course.status === 'published' ? 'Published' : 'Draft'}
            </Badge>
            {/* A HOUSE course is never reviewed by anyone (the tenant is both
                author and reviewer), so the listing state is noise. */}
            {row && !isHouse && (
              <Badge
                variant={
                  row.approvalStatus === 'approved'
                    ? 'default'
                    : row.approvalStatus === 'rejected'
                      ? 'destructive'
                      : 'secondary'
                }
                className="capitalize"
              >
                Listing {row.approvalStatus}
              </Badge>
            )}
            {/* Content re-review is a separate track from the listing decision:
                a course can be approved and listed while its newly swapped
                lessons wait for a look. House courses never enter that queue,
                so their status stays 'clear' and nothing renders here — no
                isHouse guard needed. */}
            {course.contentReviewStatus === 'pending' && (
              <Badge variant="warning">Content pending</Badge>
            )}
            {course.contentReviewStatus === 'rejected' && (
              <Badge variant="destructive" title={course.contentReviewReason ?? undefined}>
                Content rejected
              </Badge>
            )}
            <span className="text-muted-foreground">
              {course.enrollmentCount} enrolled ·{' '}
              {course.chapters.reduce((n, c) => n + c.lessons.length, 0)} lessons
            </span>
          </div>
          {row?.rejectionReason && !isHouse && (
            <p className="mt-1 text-sm text-destructive">Rejected: {row.rejectionReason}</p>
          )}
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to={`/courses/${id}/stats`}>View stats</Link>
        </Button>
      </div>

      <Tabs defaultValue="curriculum">
        {/* Seven tabs no longer fit a phone in one row — wrap rather than
            clipping the last one off-screen. */}
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="curriculum">Curriculum</TabsTrigger>
          <TabsTrigger value="cover">Cover &amp; promo</TabsTrigger>
          <TabsTrigger value="tests">Tests</TabsTrigger>
          <TabsTrigger value="certificate">Certificate</TabsTrigger>
          <TabsTrigger value="announcements">Announcements</TabsTrigger>
          <TabsTrigger value="publish">Publish</TabsTrigger>
        </TabsList>
        <TabsContent value="announcements" className="pt-4">
          <CourseAnnouncementsTab courseId={id!} />
        </TabsContent>
        <TabsContent value="details" className="pt-4">
          <DetailsTab
            course={course}
            price={row?.price}
            srp={row?.srp}
            isHouse={isHouse}
            onSaved={invalidate}
          />
        </TabsContent>
        <TabsContent value="curriculum" className="pt-4">
          <CurriculumTab course={course} onChange={invalidate} />
        </TabsContent>
        <TabsContent value="cover" className="pt-4">
          <CoverTab course={course} images={row?.images ?? []} onChange={invalidate} />
        </TabsContent>
        <TabsContent value="tests" className="pt-4">
          <CourseTestsTab course={course} />
        </TabsContent>
        <TabsContent value="certificate" className="pt-4">
          <CertificateTab course={course} onChange={invalidate} />
        </TabsContent>
        <TabsContent value="publish" className="pt-4">
          <PublishTab
            course={course}
            approvalStatus={row?.approvalStatus}
            isHouse={isHouse}
            onChange={invalidate}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Details ─────────────────────────────────────────────────────────────────

function DetailsTab({
  course,
  price,
  srp,
  isHouse,
  onSaved,
}: {
  course: CourseDetail;
  price?: string;
  srp?: string | null;
  isHouse?: boolean;
  onSaved: () => void;
}) {
  const { data: topics = [] } = useQuery({
    queryKey: ['course-categories'],
    queryFn: listCourseCategories,
  });
  const { data: allTags = [] } = useQuery({ queryKey: ['course-tags'], queryFn: listCourseTags });
  const [form, setForm] = useState({
    name: course.productName,
    price: price ?? '',
    srp: srp ?? '',
    subtitle: course.subtitle ?? '',
    level: course.level ?? '',
    categoryId: course.categoryId ?? '',
    language: course.language ?? '',
    accessLevel: course.accessLevel,
    learnPoints: course.learnPoints.join('\n'),
    requirements: course.requirements.join('\n'),
  });
  const [tagIds, setTagIds] = useState<string[]>(course.tags.map((t) => t.id));
  useEffect(() => {
    setForm((f) => ({ ...f, price: price ?? f.price, srp: srp ?? '' }));
  }, [price, srp]);

  /**
   * Category nesting is one level deep, so a flat list with the parent name
   * folded into the child's label reads the whole tree without a grouped
   * Select — roots first, each root's children right under it.
   */
  const topicOptions = useMemo(() => {
    const roots = topics.filter((t) => !t.parentId);
    const byParent = new Map<string, typeof topics>();
    for (const t of topics) {
      if (!t.parentId) continue;
      byParent.set(t.parentId, [...(byParent.get(t.parentId) ?? []), t]);
    }
    const out: Array<{ id: string; label: string }> = [];
    for (const root of roots) {
      out.push({ id: root.id, label: root.name });
      for (const child of byParent.get(root.id) ?? []) {
        out.push({ id: child.id, label: `${root.name} › ${child.name}` });
      }
    }
    // Orphans (parent inactive, so absent from this public list) would vanish
    // from the picker and silently clear the course's saved topic on save.
    for (const t of topics) {
      if (t.parentId && !roots.some((r) => r.id === t.parentId)) {
        out.push({ id: t.id, label: t.name });
      }
    }
    return out;
  }, [topics]);

  // A subscribers-only course has no price of its own (the server forces ₱0),
  // so a price the form still holds from before the switch is not a sellable
  // edit and must not warn about a review it will never trigger.
  const subscriberOnly = form.accessLevel === 'subscriber';
  const sellableChanged =
    form.name !== course.productName ||
    (!subscriberOnly && (form.price !== (price ?? '') || form.srp !== (srp ?? '')));

  const save = useMutation({
    meta: { successMessage: 'Details saved.' },
    mutationFn: () =>
      updateCourse(course.id, {
        ...(form.name !== course.productName ? { name: form.name } : {}),
        ...(subscriberOnly
          ? { price: '0', srp: null }
          : {
              ...(form.price !== (price ?? '') ? { price: form.price } : {}),
              ...(form.srp !== (srp ?? '') ? { srp: form.srp || null } : {}),
            }),
        subtitle: form.subtitle || null,
        level: (form.level || null) as 'beginner' | 'intermediate' | 'advanced' | null,
        categoryId: form.categoryId || null,
        language: form.language || null,
        tagIds,
        accessLevel: form.accessLevel,
        learnPoints: lines(form.learnPoints),
        requirements: lines(form.requirements),
      }),
    onSuccess: onSaved,
  });

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Title">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Subtitle">
            <Input
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              placeholder="One line under the title"
            />
          </Field>
          <Field label="Access">
            <Select
              value={form.accessLevel}
              onValueChange={(v) => setForm({ ...form, accessLevel: v as typeof form.accessLevel })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="purchase">Paid — one-time purchase</SelectItem>
                <SelectItem value="free">Free — anyone signed in</SelectItem>
                <SelectItem value="subscriber">Subscribers only</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Level">
            <Select
              value={form.level || 'none'}
              onValueChange={(v) => setForm({ ...form, level: v === 'none' ? '' : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Topic">
            <Select
              value={form.categoryId || 'none'}
              onValueChange={(v) => setForm({ ...form, categoryId: v === 'none' ? '' : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No topic —</SelectItem>
                {topicOptions.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Language">
            {/* Radix Select has no empty-string value, so 'none' stands in for
                "unset" here exactly as it does for Level and Topic above. */}
            <Select
              value={form.language || 'none'}
              onValueChange={(v) => setForm({ ...form, language: v === 'none' ? '' : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Not set —</SelectItem>
                {COURSE_LANGUAGES.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <TranslationsField course={course} onChanged={onSaved} />
          {subscriberOnly ? (
            <Field label="Price (₱)">
              <p className="pt-2 text-xs text-muted-foreground">
                Subscribers-only courses carry no price. Access comes from the subscription plan.
              </p>
            </Field>
          ) : (
            <>
              <Field label="Price (₱)">
                <Input
                  value={form.price}
                  inputMode="decimal"
                  disabled={form.accessLevel === 'free'}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </Field>
              <Field label="Compare-at price (₱, optional)">
                <Input
                  value={form.srp}
                  inputMode="decimal"
                  onChange={(e) => setForm({ ...form, srp: e.target.value })}
                  placeholder="Shown struck through"
                />
              </Field>
            </>
          )}
        </div>
        <Field label="What you'll learn (one per line)">
          <Textarea
            value={form.learnPoints}
            onChange={(e) => setForm({ ...form, learnPoints: e.target.value })}
            rows={4}
          />
        </Field>
        <Field label="Requirements (one per line)">
          <Textarea
            value={form.requirements}
            onChange={(e) => setForm({ ...form, requirements: e.target.value })}
            rows={3}
          />
        </Field>
        {/* Rendered even with an empty registry. Hiding the whole field made
            tags look like a feature that did not exist — the author has no way
            to tell "no tags yet" from "not a thing here". */}
        <Field label="Tags (max 10)">
          {allTags.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">
              No tags yet. An administrator creates them under Content &rarr; Course Tags.
            </p>
          ) : (
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {allTags.map((t) => {
                const checked = tagIds.includes(t.id);
                return (
                  <label key={t.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={checked}
                      // The API caps the replace-set at 10; blocking the 11th
                      // here beats a rejected save the creator can't explain.
                      disabled={!checked && tagIds.length >= 10}
                      onCheckedChange={(v) =>
                        setTagIds((prev) =>
                          v === true ? [...prev, t.id] : prev.filter((id) => id !== t.id),
                        )
                      }
                    />
                    {t.name}
                  </label>
                );
              })}
            </div>
          )}
        </Field>
        {/* Only the sellable fields (title / price / compare-at) re-open admin
            review — topic, language and tags are catalog metadata, so they must
            not raise this warning. House courses are never re-reviewed at all:
            the tenant is its own reviewer. */}
        {sellableChanged && !isHouse && (
          <p className="text-xs text-amber-700">
            Changing the title or price sends the listing back to admin review; the course stays
            visible only after it is re-approved.
          </p>
        )}
        <Button onClick={() => save.mutate()} disabled={save.isPending || !form.name}>
          {save.isPending ? 'Saving…' : 'Save details'}
        </Button>
      </CardContent>
    </Card>
  );
}

const TRANSLATION_ERROR_COPY: Record<string, string> = {
  translation_language_required:
    'Both courses need a content language first. Set one on the other course and save it.',
  translation_same_language:
    'That course is in the same language as this one — a set holds one course per language.',
  translation_language_taken:
    'That set already has a course in this language. Unlink that one first, or pick another course.',
  translation_same_course: 'A course cannot be a translation of itself.',
  not_found: 'That course is no longer available.',
};

/**
 * Translation links. A translated edition is its OWN course (own video, own
 * captions, own instructor voice) — the courses are grouped, never merged — so
 * this block only ever manages membership of a group.
 *
 * Spans the whole Details grid row: a list of siblings with an action each does
 * not fit the half-width cell the Language picker sits in, and it belongs
 * directly under that picker because the language is what it keys off.
 */
function TranslationsField({ course, onChanged }: { course: CourseDetail; onChanged: () => void }) {
  const { data: rows = [] } = useQuery({ queryKey: ['creator-courses'], queryFn: listMyCourses });
  const [pick, setPick] = useState('');

  /*
   * Everything below keys off the SAVED language, not the Details form's
   * unsaved one: the API validates against the database, so filtering by a
   * language the creator has picked but not yet saved would offer candidates
   * the server then refuses for reasons the screen contradicts.
   */
  const language = course.language;
  const linkedIds = new Set(course.translations.map((t) => t.courseId));
  const candidates = rows.filter(
    (r) => r.id !== course.id && !!r.language && r.language !== language && !linkedIds.has(r.id),
  );

  const link = useMutation({
    meta: {
      successMessage: 'Translation linked.',
      // Every code below has copy of its own, rendered under the picker where
      // the fix is — a toast would take the explanation away from the control.
      silentErrorCodes: Object.keys(TRANSLATION_ERROR_COPY),
    },
    mutationFn: (targetCourseId: string) => linkCourseTranslation(course.id, targetCourseId),
    onSuccess: () => {
      setPick('');
      onChanged();
    },
  });
  const unlink = useMutation({
    meta: { successMessage: 'Translation unlinked.' },
    /*
     * DELETE unlinks the course NAMED IN THE URL and leaves the rest of the
     * group intact, so a per-row Unlink passes that ROW's course id. Passing
     * my own id would drop me out and leave the siblings linked to each other
     * — the opposite of what the button next to one sibling promises.
     */
    mutationFn: (siblingCourseId: string) => unlinkCourseTranslation(siblingCourseId),
    onSuccess: onChanged,
  });

  const errCode = apiErrorPayload(link.error)?.error;
  const linkError = errCode ? (TRANSLATION_ERROR_COPY[errCode] ?? errCode) : null;

  return (
    <div className="space-y-2 rounded border p-3 sm:col-span-2">
      <p className="text-sm font-medium">Translations</p>
      <p className="text-xs text-muted-foreground">
        A translated edition is a separate course in its own language. Link them and each one&apos;s
        page points at the others. This is catalog metadata — linking never sends your listing back
        to review.
      </p>
      {course.translations.length === 0 ? (
        <p className="text-xs text-muted-foreground">No translations linked yet.</p>
      ) : (
        <ul className="space-y-1">
          {course.translations.map((t) => {
            // The public "also available in" list shows published AND approved
            // siblings only, so a draft or unapproved one is linked here but
            // invisible to learners. Say which, or the author reads the empty
            // storefront as a broken link.
            const hint =
              t.status !== 'published'
                ? 'Draft — learners see the link once it is published'
                : t.approvalStatus !== 'approved'
                  ? `Listing ${t.approvalStatus} — learners see the link once it is approved`
                  : null;
            return (
              <li key={t.courseId} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium">
                  {courseLanguageLabel(t.language) ?? 'No language'}
                </span>
                <Link to={`/courses/${t.courseId}`} className="font-medium hover:underline">
                  {t.name}
                </Link>
                {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto"
                  disabled={unlink.isPending}
                  onClick={() => unlink.mutate(t.courseId)}
                >
                  Unlink
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {/* The API rejects a link when either side has no language. Saying so up
          front beats letting the creator find it in an error toast. */}
      {!language ? (
        <p className="text-xs text-amber-700">
          Pick this course&apos;s Language above and save it before linking a translation.
        </p>
      ) : candidates.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No other course of yours is set to a different language yet.
        </p>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger className="sm:flex-1">
              <SelectValue placeholder="Link a translation…" />
            </SelectTrigger>
            <SelectContent>
              {candidates.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {courseLanguageLabel(c.language)} — {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            disabled={!pick || link.isPending}
            onClick={() => link.mutate(pick)}
          >
            {link.isPending ? 'Linking…' : 'Link'}
          </Button>
        </div>
      )}
      {linkError && <p className="text-xs text-destructive">{linkError}</p>}
    </div>
  );
}

// ── Curriculum ──────────────────────────────────────────────────────────────

function CurriculumTab({ course, onChange }: { course: CourseDetail; onChange: () => void }) {
  const [newChapter, setNewChapter] = useState('');
  const addChapter = useMutation({
    mutationFn: () =>
      createChapter(course.id, { title: newChapter, sortOrder: course.chapters.length }),
    onSuccess: () => {
      setNewChapter('');
      onChange();
    },
  });
  const moveChapter = useMutation({
    mutationFn: (ids: string[]) => reorderChapters(course.id, ids),
    onSuccess: onChange,
  });

  const ids = course.chapters.map((c) => c.id);
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    const next = [...ids];
    [next[i], next[j]] = [next[j]!, next[i]!];
    moveChapter.mutate(next);
  };

  return (
    <div className="space-y-4">
      {course.chapters.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No chapters yet. Add a chapter, then add lessons to it.
        </p>
      )}
      {course.chapters.map((ch, i) => (
        <ChapterCard
          key={ch.id}
          chapter={ch}
          index={i}
          count={course.chapters.length}
          onMove={(dir) => move(i, dir)}
          onChange={onChange}
        />
      ))}
      <Card>
        <CardContent className="flex items-center gap-2 pt-6">
          <Input
            value={newChapter}
            onChange={(e) => setNewChapter(e.target.value)}
            placeholder="New chapter title"
            onKeyDown={(e) => e.key === 'Enter' && newChapter && addChapter.mutate()}
          />
          <Button
            onClick={() => addChapter.mutate()}
            disabled={!newChapter || addChapter.isPending}
          >
            Add chapter
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function ChapterCard({
  chapter,
  index,
  count,
  onMove,
  onChange,
}: {
  chapter: Chapter;
  index: number;
  count: number;
  onMove: (dir: -1 | 1) => void;
  onChange: () => void;
}) {
  const [title, setTitle] = useState(chapter.title);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newLesson, setNewLesson] = useState('');
  useEffect(() => setTitle(chapter.title), [chapter.title]);

  const rename = useMutation({
    mutationFn: () => updateChapter(chapter.id, { title }),
    onSuccess: onChange,
  });
  // The 409 is handled here (a confirm dialog), not by the global error toast.
  const [guard, setGuard] = useState<{ learnerCount: number; attemptCount: number } | null>(null);
  const remove = useMutation({
    meta: { successMessage: 'Chapter deleted.', silentErrorCodes: ['has_learner_progress'] },
    mutationFn: (force?: boolean) => deleteChapter(chapter.id, { force }),
    onSuccess: () => {
      setGuard(null);
      onChange();
    },
    onError: (err) => {
      const body = apiErrorPayload(err);
      if (body?.error === 'has_learner_progress') {
        setConfirmDelete(false);
        setGuard({
          learnerCount: Number(body.learnerCount ?? 0),
          attemptCount: Number(body.attemptCount ?? 0),
        });
      }
    },
  });
  const addLesson = useMutation({
    mutationFn: () =>
      createLesson(chapter.id, { title: newLesson, sortOrder: chapter.lessons.length }),
    onSuccess: () => {
      setNewLesson('');
      onChange();
    },
  });
  const moveLesson = useMutation({
    mutationFn: (ids: string[]) => reorderLessons(chapter.id, ids),
    onSuccess: onChange,
  });
  const lessonIds = chapter.lessons.map((l) => l.id);
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= lessonIds.length) return;
    const next = [...lessonIds];
    [next[i], next[j]] = [next[j]!, next[i]!];
    moveLesson.mutate(next);
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <span className="text-xs text-muted-foreground w-6">#{index + 1}</span>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title !== chapter.title && rename.mutate()}
          className="font-medium"
        />
        <Button size="sm" variant="ghost" onClick={() => onMove(-1)} disabled={index === 0}>
          ↑
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onMove(1)} disabled={index === count - 1}>
          ↓
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
          Delete
        </Button>
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title="Delete this chapter?"
          description="All lessons in it are deleted too. Learner progress on those lessons is lost."
          confirmLabel="Delete"
          confirmVariant="destructive"
          onConfirm={() => remove.mutate(undefined)}
        />
        <ConfirmDialog
          open={guard !== null}
          onOpenChange={(o) => !o && setGuard(null)}
          title={`${guard?.learnerCount ?? 0} learner${guard?.learnerCount === 1 ? '' : 's'} have progress here`}
          description={`Deleting this chapter also deletes its quiz${
            guard?.attemptCount ? ` and ${guard.attemptCount} graded attempt(s)` : ''
          }, and the Q&A threads and notes on its lessons. This cannot be undone.`}
          confirmLabel="Delete anyway"
          confirmVariant="destructive"
          onConfirm={() => remove.mutate(true)}
        />
      </CardHeader>
      <CardContent className="space-y-2">
        {chapter.lessons.map((l, i) => (
          <LessonRow
            key={l.id}
            lesson={l}
            index={i}
            count={chapter.lessons.length}
            onMove={(dir) => move(i, dir)}
            onChange={onChange}
          />
        ))}
        <div className="flex items-center gap-2 pt-1">
          <Input
            value={newLesson}
            onChange={(e) => setNewLesson(e.target.value)}
            placeholder="New lesson title"
            onKeyDown={(e) => e.key === 'Enter' && newLesson && addLesson.mutate()}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => addLesson.mutate()}
            disabled={!newLesson || addLesson.isPending}
          >
            Add lesson
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function LessonRow({
  lesson,
  index,
  count,
  onMove,
  onChange,
}: {
  lesson: Lesson;
  index: number;
  count: number;
  onMove: (dir: -1 | 1) => void;
  onChange: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: lesson.title,
    description: lesson.description ?? '',
    sourceType: lesson.sourceType,
    embedUrl: lesson.embedUrl ?? '',
    body: lesson.body ?? '',
    durationSeconds: lesson.durationSeconds != null ? formatClock(lesson.durationSeconds) : '',
    isPreview: lesson.isPreview,
    availableAfterDays:
      lesson.availableAfterDays && lesson.availableAfterDays > 0
        ? String(lesson.availableAfterDays)
        : '',
    availableFrom: toLocalInput(lesson.availableFrom),
  });
  /**
   * One state for the whole upload, because "is it uploading?" and "how far?"
   * were being answered by two different things and finalize (which can take a
   * while on a big file) looked identical to a stalled 100 % upload.
   */
  const [uploadState, setUploadState] = useState<{
    phase: 'idle' | 'uploading' | 'finalizing' | 'error' | 'cancelled';
    pct: number;
    partsDone: number;
    partsTotal: number;
  }>({ phase: 'idle', pct: 0, partsDone: 0, partsTotal: 0 });
  const abortRef = useRef<AbortController | null>(null);
  // Already cached by the page's own query — this is a read, not a request.
  const { data: me } = useQuery({ queryKey: ['creator-me'], queryFn: getCreatorMe });
  const limits = me?.limits;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRemoveVideo, setConfirmRemoveVideo] = useState(false);
  // Tracks whether the AUTHOR touched the duration box. An untouched field is
  // omitted from the save entirely, so a value the server just extracted at
  // finalize is never blanked by an unrelated edit.
  const [durationTouched, setDurationTouched] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const captionFileRef = useRef<HTMLInputElement>(null);
  const [captionDraft, setCaptionDraft] = useState({ lang: '', label: '' });
  /*
   * The caption schema still takes ANY BCP-47-ish code, and rows written
   * before this picker (or by the API directly) may hold one the curated list
   * never offered. A Select silently drops a value with no matching item, so
   * an off-list code is appended as its own option — replacing such a caption
   * must not quietly rewrite its language to whatever the picker shows first.
   */
  const captionLangOptions = useMemo(() => {
    const code = captionDraft.lang.trim().toLowerCase();
    const curated = COURSE_LANGUAGES.map((l) => ({
      code: l.code as string,
      label: l.label as string,
    }));
    if (!code || curated.some((l) => l.code === code)) return curated;
    return [...curated, { code, label: courseLanguageLabel(code) ?? code.toUpperCase() }];
  }, [captionDraft.lang]);
  // The re-sync below fires on every parent refetch (a sibling save, a new
  // chapter), not only when THIS row changed — so it must not clobber the
  // author's in-flight edits. Compare against what the server last told us.
  const syncedRef = useRef<string>('');
  useEffect(() => {
    const signature = JSON.stringify([
      lesson.title,
      lesson.description,
      lesson.sourceType,
      lesson.embedUrl,
      lesson.body,
      lesson.durationSeconds,
      lesson.isPreview,
      lesson.availableAfterDays,
      lesson.availableFrom,
    ]);
    if (syncedRef.current === signature) return;
    syncedRef.current = signature;
    setForm({
      title: lesson.title,
      description: lesson.description ?? '',
      sourceType: lesson.sourceType,
      embedUrl: lesson.embedUrl ?? '',
      body: lesson.body ?? '',
      durationSeconds: lesson.durationSeconds != null ? formatClock(lesson.durationSeconds) : '',
      isPreview: lesson.isPreview,
      availableAfterDays:
        lesson.availableAfterDays && lesson.availableAfterDays > 0
          ? String(lesson.availableAfterDays)
          : '',
      availableFrom: toLocalInput(lesson.availableFrom),
    });
    setDurationTouched(false);
  }, [lesson]);

  const parsedDuration = durationTouched ? parseDurationInput(form.durationSeconds) : null;
  const durationInvalid = parsedDuration === 'invalid';
  const daysInvalid =
    form.availableAfterDays !== '' &&
    (!/^\d{1,4}$/.test(form.availableAfterDays) || Number(form.availableAfterDays) > 3650);
  const fromInvalid = form.availableFrom !== '' && Number.isNaN(Date.parse(form.availableFrom));

  const save = useMutation({
    meta: { successMessage: 'Lesson saved.' },
    mutationFn: () =>
      updateLesson(lesson.id, {
        title: form.title,
        description: form.description || null,
        sourceType: form.sourceType,
        embedUrl: form.sourceType === 'embed' ? form.embedUrl || null : null,
        body: form.sourceType === 'article' ? form.body || null : null,
        // Omitted when untouched: the schema field is optional, so leaving it
        // out is a no-op rather than an explicit "clear this".
        ...(durationTouched && parsedDuration !== 'invalid'
          ? { durationSeconds: parsedDuration }
          : {}),
        isPreview: form.isPreview,
        availableAfterDays: form.availableAfterDays ? Number(form.availableAfterDays) : null,
        availableFrom: form.availableFrom ? new Date(form.availableFrom).toISOString() : null,
      }),
    onSuccess: onChange,
  });
  const [guard, setGuard] = useState<{ learnerCount: number } | null>(null);
  const remove = useMutation({
    meta: { successMessage: 'Lesson deleted.', silentErrorCodes: ['has_learner_progress'] },
    mutationFn: (force?: boolean) => deleteLesson(lesson.id, { force }),
    onSuccess: () => {
      setGuard(null);
      onChange();
    },
    onError: (err) => {
      const body = apiErrorPayload(err);
      if (body?.error === 'has_learner_progress') {
        setConfirmDelete(false);
        setGuard({ learnerCount: Number(body.learnerCount ?? 0) });
      }
    },
  });
  const upload = useMutation({
    meta: {
      successMessage: 'Video uploaded and verified.',
      // Cancelling is not a failure, and the cap is reported inline with the
      // actual numbers rather than as a bare server code.
      silentErrorCodes: ['video_upload_cancelled'],
    },
    mutationFn: async (file: File) => {
      if (!limits) throw new Error('Upload limits are still loading — try again in a moment.');
      if (file.size > limits.maxBytes) {
        // Refuse before any request: a 900 MB file should not have to reach the
        // server to be told it is too big.
        throw new Error(
          `That file is ${formatMb(file.size)}. The limit is ${formatMb(limits.maxBytes)}.`,
        );
      }
      const controller = new AbortController();
      abortRef.current = controller;
      setUploadState({ phase: 'uploading', pct: 0, partsDone: 0, partsTotal: 0 });
      // A staged upload of the SAME file resumes instead of starting over.
      const staged = lesson.pendingUploadId
        ? await getVideoUploadStatus(lesson.id).catch(() => undefined)
        : undefined;
      return uploadLessonVideo(
        lesson.id,
        file,
        limits,
        {
          signal: controller.signal,
          onProgress: (pct, detail) =>
            setUploadState((st) =>
              st.phase === 'cancelled'
                ? st
                : {
                    phase: pct >= 100 ? 'finalizing' : 'uploading',
                    pct,
                    partsDone: detail.partsDone,
                    partsTotal: detail.partsTotal,
                  },
            ),
        },
        staged,
      );
    },
    onError: (err) => {
      setUploadState((st) => ({
        ...st,
        phase: err instanceof UploadCancelled ? 'cancelled' : 'error',
      }));
    },
    onSettled: () => {
      abortRef.current = null;
      setUploadState((st) =>
        st.phase === 'error' || st.phase === 'cancelled'
          ? st
          : { phase: 'idle', pct: 0, partsDone: 0, partsTotal: 0 },
      );
      // Without this, re-picking the SAME file after a failure fires no change
      // event and the upload control looks dead.
      if (fileRef.current) fileRef.current.value = '';
    },
    onSuccess: (result) => {
      // Show the extracted duration immediately, so a Save clicked before the
      // parent refetch lands cannot write the old (empty) value back.
      if (result.durationSeconds != null) {
        setForm((f) => ({ ...f, durationSeconds: formatClock(result.durationSeconds as number) }));
        setDurationTouched(false);
      }
      onChange();
    },
  });
  const clear = useMutation({
    mutationFn: () => clearLessonVideo(lesson.id),
    onSuccess: () => {
      setConfirmRemoveVideo(false);
      onChange();
    },
  });
  const cancelUpload = useMutation({
    mutationFn: async () => {
      abortRef.current?.abort();
      const staged = await getVideoUploadStatus(lesson.id).catch(() => null);
      if (staged?.key) {
        await cancelLessonVideoUpload(lesson.id, staged.key, staged.uploadId ?? undefined);
      }
    },
    onSuccess: () => {
      setUploadState({ phase: 'idle', pct: 0, partsDone: 0, partsTotal: 0 });
      onChange();
    },
  });
  /**
   * Play this lesson as the SERVER resolves it — the staged version under
   * `hold`, the live one otherwise. Server-resolved rather than built from the
   * form, because an R2 lesson needs a presigned URL the editor cannot mint.
   */
  const [selfPreviewSource, setSelfPreviewSource] = useState<LessonPreview | null>(null);
  const selfPreview = useMutation({
    mutationFn: () => openLessonPreview(lesson.id),
    onSuccess: setSelfPreviewSource,
  });

  const addCaption = useMutation({
    meta: {
      successMessage: 'Captions uploaded.',
      silentErrorCodes: ['unsupported_file_type', 'file_too_large'],
    },
    mutationFn: (file: File) =>
      uploadLessonCaption(lesson.id, {
        file,
        lang: captionDraft.lang.trim().toLowerCase(),
        label: captionDraft.label.trim() || captionDraft.lang.trim().toUpperCase(),
      }),
    onSettled: () => {
      if (captionFileRef.current) captionFileRef.current.value = '';
    },
    onSuccess: () => {
      setCaptionDraft({ lang: '', label: '' });
      onChange();
    },
    onError: (err) => {
      // A rejected file used to look like a successful save that never showed
      // up in the list, because the player is where the bad bytes were dropped.
      const code = apiErrorPayload(err)?.error;
      toast.error(
        code === 'unsupported_file_type'
          ? 'That file is not WebVTT — it must start with a WEBVTT line. Export the captions as .vtt and try again.'
          : code === 'file_too_large'
            ? 'That caption file is too large.'
            : 'Could not upload the captions — try again.',
      );
    },
  });
  const removeCaption = useMutation({
    mutationFn: (captionId: string) => deleteLessonCaption(lesson.id, captionId),
    onSuccess: onChange,
  });

  // Live preview of an embed URL. Doubles as the duration probe for embeds —
  // the provider reports it over the same postMessage channel the learner's
  // player uses, so an author never has to type one by hand.
  const [previewUrl, setPreviewUrl] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setPreviewUrl(form.embedUrl.trim()), 600);
    return () => clearTimeout(timer);
  }, [form.embedUrl]);
  const preview = useMemo(() => {
    if (form.sourceType !== 'embed' || !previewUrl) return null;
    const embed = resolveRpEmbed(previewUrl);
    if (!embed || embed.kind !== 'iframe') return null;
    const provider = detectPlaybackProvider(embed.src);
    if (provider === 'unknown') return null;
    return {
      src: controlSrc(embed.src, provider, {
        origin: typeof window === 'undefined' ? '' : window.location.origin,
        playsinline: true,
      }),
      provider,
    };
  }, [form.sourceType, previewUrl]);

  // A staged upload is a state the LESSON is not in: the badge keeps saying
  // "Video ready" while a replacement uploads, because that is what learners
  // are seeing. Only a lesson with no live video reads as "Uploading…".
  const sourceBadge =
    lesson.sourceType === 'article'
      ? lesson.body
        ? 'Article'
        : 'No content'
      : lesson.sourceType === 'r2'
        ? lesson.videoStatus === 'ready'
          ? 'Video ready'
          : lesson.pendingVideoKey
            ? 'Uploading…'
            : 'No video'
        : lesson.embedUrl
          ? 'Embed'
          : 'No video';
  const hasDrip =
    !lesson.isPreview && ((lesson.availableAfterDays ?? 0) > 0 || !!lesson.availableFrom);
  const fileCount = (lesson.files ?? []).length;
  const allowedEmbedHosts = me?.limits?.embedHosts ?? [];
  // Checked client-side purely so the author hears about it while typing; the
  // SERVICE is the actual gate (this list can be stale, and a hand-rolled
  // request would bypass it entirely).
  const embedHostProblem =
    form.sourceType === 'embed' &&
    form.embedUrl.trim() &&
    allowedEmbedHosts.length > 0 &&
    !isAllowedEmbedHost(form.embedUrl.trim(), allowedEmbedHosts)
      ? 'That host is not on this site’s allowed list, so the lesson will not save.'
      : null;

  /**
   * What switching the lesson type will discard. Two surfaces, because an
   * inline hint alone was missed: `describeSwitchLoss` also drives a CONFIRM
   * dialog on the picker, since the author who is about to lose an uploaded
   * video is the one least likely to read an 11px line under the field.
   * Nothing is destroyed until Save, so the dialog is a warning, not a commit.
   */
  const describeSwitchLoss = (next: Lesson['sourceType']): string | null => {
    if (next === lesson.sourceType) return null;
    if (lesson.sourceType === 'article' && lesson.body) {
      return 'Saving will clear the article text.';
    }
    if (lesson.sourceType === 'embed' && lesson.embedUrl) {
      return 'Saving will clear the video link.';
    }
    // Any staged or finished upload counts, not just `ready` — a lesson mid
    // upload has bytes to lose too, and `none` is the only empty state.
    if (lesson.sourceType === 'r2' && lesson.videoStatus !== 'none') {
      return 'Saving detaches the uploaded video. Switch back to \u201cUpload\u201d to use it again.';
    }
    return null;
  };
  const switchHint = describeSwitchLoss(form.sourceType);
  const [pendingSwitch, setPendingSwitch] = useState<{
    next: Lesson['sourceType'];
    loss: string;
  } | null>(null);

  return (
    <div className="rounded border">
      <div className="flex items-center gap-2 p-2">
        <span className="text-xs text-muted-foreground w-6">{index + 1}.</span>
        <button
          className="flex-1 text-left text-sm hover:underline"
          onClick={() => setOpen((o) => !o)}
        >
          {lesson.title}
        </button>
        {lesson.isPreview && <Badge variant="outline">Preview</Badge>}
        {/* `hold` mode: what the badges above describe is the version the
            creator submitted, NOT the one their students are watching. Saying
            so here is the whole difference between "my upload worked" and "my
            upload replaced what people paid for". */}
        {lesson.heldPending && <Badge variant="warning">Awaiting approval</Badge>}
        {hasDrip && <Badge variant="outline">Drip</Badge>}
        {fileCount > 0 && (
          <Badge variant="outline">
            {fileCount} file{fileCount === 1 ? '' : 's'}
          </Badge>
        )}
        <Badge
          variant={
            sourceBadge === 'No video' ||
            sourceBadge === 'No content' ||
            sourceBadge === 'Uploading…'
              ? 'secondary'
              : 'default'
          }
        >
          {sourceBadge}
        </Badge>
        <Button size="sm" variant="ghost" onClick={() => onMove(-1)} disabled={index === 0}>
          ↑
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onMove(1)} disabled={index === count - 1}>
          ↓
        </Button>
      </div>
      {open && (
        <div className="space-y-3 border-t p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Title">
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </Field>
            <Field label="Duration (mm:ss)">
              <Input
                value={form.durationSeconds}
                placeholder="12:34"
                onChange={(e) => {
                  setDurationTouched(true);
                  setForm({ ...form, durationSeconds: e.target.value });
                }}
              />
              {durationInvalid ? (
                <p className="mt-1 text-[11px] text-destructive">
                  Use mm:ss, h:mm:ss, or a number of seconds.
                </p>
              ) : (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {form.sourceType === 'article'
                    ? 'Optional — an estimated reading time, counted in the course total.'
                    : 'Filled in automatically when you upload a video or paste a link.'}
                </p>
              )}
            </Field>
          </div>
          <Field label="Notes (shown under the player)">
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
            />
          </Field>
          <Field label="Lesson type">
            <Select
              value={form.sourceType}
              onValueChange={(v) => {
                const next = v as Lesson['sourceType'];
                const loss = describeSwitchLoss(next);
                if (loss) setPendingSwitch({ next, loss });
                else setForm({ ...form, sourceType: next });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="embed">YouTube / Vimeo / video link</SelectItem>
                <SelectItem value="r2">Upload a video file (MP4)</SelectItem>
                <SelectItem value="article">Article — written lesson</SelectItem>
              </SelectContent>
            </Select>
            {switchHint && <p className="mt-1 text-[11px] text-muted-foreground">{switchHint}</p>}
          </Field>
          {form.sourceType === 'article' ? (
            <Field label="Article">
              <RichTextEditor
                value={form.body}
                onChange={(html) => setForm((f) => ({ ...f, body: html }))}
                placeholder="Write the lesson…"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Headings, lists, links and emphasis are kept. Images, embeds and scripts are removed
                when you save.
              </p>
            </Field>
          ) : form.sourceType === 'embed' ? (
            <div className="space-y-2">
              <Field label="Video URL">
                <Input
                  value={form.embedUrl}
                  onChange={(e) => setForm({ ...form, embedUrl: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=… or https://vimeo.com/…"
                />
                {/* Say it BEFORE the save fails. Silence was the old behaviour:
                    an unsupported host simply rendered no preview. */}
                {embedHostProblem ? (
                  <p className="mt-1 text-[11px] text-destructive">{embedHostProblem}</p>
                ) : (
                  allowedEmbedHosts.length > 0 && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Allowed: {allowedEmbedHosts.join(', ')} — or a direct .mp4 link over https.
                    </p>
                  )
                )}
              </Field>
              {preview && (
                <div className="space-y-1">
                  <div className="aspect-video w-full max-w-sm overflow-hidden rounded">
                    <VideoPlayer
                      source={{ kind: 'iframe', src: preview.src, provider: preview.provider }}
                      title="Lesson preview"
                      protocolId={`editor-${lesson.id}`}
                      chrome="none"
                      preferences={{ rate: 1, captionsLang: null }}
                      onEvent={(event) => {
                        const seconds =
                          event.type === 'duration'
                            ? event.duration
                            : event.type === 'time'
                              ? event.duration
                              : undefined;
                        if (!seconds || durationTouched || form.durationSeconds) return;
                        setDurationTouched(true);
                        setForm((f) => ({
                          ...f,
                          durationSeconds: formatClock(Math.round(seconds)),
                        }));
                      }}
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Preview — the duration fills in automatically once the player loads.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {lesson.pendingUploadId && !upload.isPending && (
                <div className="space-y-1 rounded border border-amber-300 bg-amber-50 p-2 dark:border-amber-800 dark:bg-amber-950/40">
                  <p className="text-xs font-medium">Upload unfinished</p>
                  <p className="text-[11px] text-muted-foreground">
                    An upload for this lesson was interrupted. Pick the same file again to carry on
                    from where it stopped, or cancel it.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={cancelUpload.isPending}
                    onClick={() => cancelUpload.mutate()}
                  >
                    Cancel upload
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Input
                  ref={fileRef}
                  type="file"
                  accept="video/mp4"
                  disabled={upload.isPending || !limits}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) upload.mutate(f);
                  }}
                />
                {lesson.videoStatus === 'ready' && !upload.isPending && (
                  <Button size="sm" variant="outline" onClick={() => setConfirmRemoveVideo(true)}>
                    Remove video
                  </Button>
                )}
                {/* Watch your own upload. Before this there was no way to: the
                    editor previewed embeds client-side and R2 not at all, which
                    was merely awkward until `hold` made the creator's new video
                    the one thing nobody could see. */}
                {(lesson.videoStatus === 'ready' || lesson.heldPending) && !upload.isPending && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={selfPreview.isPending}
                    onClick={() => selfPreview.mutate()}
                  >
                    {lesson.heldPending ? 'Preview new video' : 'Preview'}
                  </Button>
                )}
                {upload.isPending && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={cancelUpload.isPending}
                    onClick={() => cancelUpload.mutate()}
                  >
                    Cancel
                  </Button>
                )}
              </div>
              {(uploadState.phase === 'uploading' || uploadState.phase === 'finalizing') && (
                <div className="space-y-1">
                  <Progress value={uploadState.pct} />
                  <p className="text-[11px] text-muted-foreground">
                    {uploadState.phase === 'finalizing'
                      ? 'Checking the file…'
                      : uploadState.partsTotal > 1
                        ? `Uploading ${uploadState.pct}% — part ${uploadState.partsDone + 1} of ${uploadState.partsTotal}. You can close this page and carry on later.`
                        : `Uploading ${uploadState.pct}%`}
                  </p>
                </div>
              )}
              {uploadState.phase === 'cancelled' && (
                <p className="text-[11px] text-muted-foreground">Upload cancelled.</p>
              )}
              {/* The single most reassuring thing to say to an author replacing
                  a video on a course people are already watching. */}
              {lesson.heldPending ? (
                <p className="text-[11px] text-muted-foreground">
                  Your students keep watching the approved video. This one goes live when an
                  administrator approves the change.
                </p>
              ) : (
                lesson.videoStatus === 'ready' && (
                  <p className="text-[11px] text-muted-foreground">
                    Your current video keeps playing until the new one has been checked.
                  </p>
                )
              )}
              <p className="text-[11px] text-muted-foreground">
                MP4 (H.264/AAC) plays everywhere
                {limits ? `, up to ${formatMb(limits.maxBytes)}` : ''}. Large files upload in pieces
                and can be resumed. Save the lesson before uploading if you just switched to this
                source.
              </p>
              <Dialog
                open={!!selfPreviewSource}
                onOpenChange={(o) => !o && setSelfPreviewSource(null)}
              >
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>
                      {selfPreviewSource?.held ? 'Your new version (awaiting approval)' : 'Preview'}
                    </DialogTitle>
                  </DialogHeader>
                  {selfPreviewSource?.kind === 'article' ? (
                    <div
                      className="prose prose-sm max-w-none dark:prose-invert"
                      dangerouslySetInnerHTML={{ __html: selfPreviewSource.body }}
                    />
                  ) : (
                    selfPreviewSource && (
                      <div className="aspect-video w-full overflow-hidden rounded">
                        <VideoPlayer
                          source={
                            selfPreviewSource.kind === 'video'
                              ? { kind: 'video', src: selfPreviewSource.src, captions: [] }
                              : {
                                  kind: 'iframe',
                                  src: selfPreviewSource.src,
                                  provider: detectPlaybackProvider(selfPreviewSource.src),
                                }
                          }
                          title={selfPreviewSource.title}
                          protocolId={`self-preview-${lesson.id}`}
                          chrome={selfPreviewSource.kind === 'video' ? 'native' : 'none'}
                          preferences={{ rate: 1, captionsLang: null }}
                        />
                      </div>
                    )
                  )}
                  {selfPreviewSource?.held && (
                    <p className="text-xs text-muted-foreground">
                      This is what you submitted. Your students keep the approved version until an
                      administrator approves this one.
                    </p>
                  )}
                </DialogContent>
              </Dialog>
              {confirmRemoveVideo && (
                <div className="space-y-2 rounded border border-destructive/50 p-2">
                  <p className="text-xs">
                    Remove this video? Learners lose access to it immediately. The file is kept for
                    a short grace period before it is deleted.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={clear.isPending}
                      onClick={() => clear.mutate()}
                    >
                      Remove video
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmRemoveVideo(false)}
                    >
                      Keep it
                    </Button>
                  </div>
                </div>
              )}
              {/* Shown for every uploaded-video lesson, not only a finished
                  one. Gating the whole panel on `ready` hid the rows an author
                  had just written the moment anything reset the video state,
                  which reads as "it saved but never appeared". */}
              <div className="space-y-2 rounded border p-2">
                <p className="text-xs font-medium">Captions</p>
                {(lesson.captions ?? []).length === 0 ? (
                  <p className="text-[11px] text-muted-foreground">
                    No captions yet. WebVTT (.vtt) only — embedded videos use their provider&apos;s
                    own captions.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {(lesson.captions ?? []).map((c) => (
                      <li key={c.id} className="flex items-center gap-2 text-xs">
                        <span className="rounded bg-muted px-1.5 py-0.5 font-medium uppercase">
                          {c.lang}
                        </span>
                        <span className="flex-1 truncate">{c.label}</span>
                        {/* Re-uploading an existing language replaces it, so
                              seeding the draft from this row is the whole
                              replace flow — and the seeded code may be one the
                              curated picker does not carry (see
                              captionLangOptions). */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setCaptionDraft({ lang: c.lang, label: c.label })}
                        >
                          Replace
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={removeCaption.isPending}
                          onClick={() => removeCaption.mutate(c.id)}
                        >
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Select
                    value={captionDraft.lang}
                    onValueChange={(v) =>
                      setCaptionDraft((d) => ({
                        lang: v,
                        // Fill the label from the curated name, but never
                        // overwrite one the author typed themselves.
                        label: d.label.trim() ? d.label : (courseLanguageLabel(v) ?? ''),
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Caption language" />
                    </SelectTrigger>
                    <SelectContent>
                      {captionLangOptions.map((l) => (
                        <SelectItem key={l.code} value={l.code}>
                          {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={captionDraft.label}
                    placeholder="Label (English)"
                    onChange={(e) => setCaptionDraft({ ...captionDraft, label: e.target.value })}
                  />
                </div>
                <Input
                  ref={captionFileRef}
                  type="file"
                  accept=".vtt,text/vtt"
                  disabled={!captionDraft.lang.trim() || addCaption.isPending}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) addCaption.mutate(f);
                  }}
                />
                <p className="text-[11px] text-muted-foreground">
                  {lesson.videoStatus === 'ready'
                    ? 'Uploading a language that already exists replaces it.'
                    : 'Uploading a language that already exists replaces it. Captions play once this lesson\u2019s video upload finishes.'}
                </p>
              </div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.isPreview}
              onCheckedChange={(v) => setForm({ ...form, isPreview: v === true })}
            />
            Free preview — anyone can watch this lesson without buying the course
          </label>
          <div className="space-y-2 rounded border p-2">
            <p className="text-xs font-medium">Release</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Unlock after (days from enrollment)">
                <Input
                  inputMode="numeric"
                  placeholder="0 = immediately"
                  value={form.availableAfterDays}
                  onChange={(e) => setForm({ ...form, availableAfterDays: e.target.value })}
                />
                {daysInvalid && (
                  <p className="mt-1 text-[11px] text-destructive">Whole days, 0–3650.</p>
                )}
              </Field>
              <Field label="Unlock on (date & time)">
                <div className="flex items-center gap-2">
                  <Input
                    type="datetime-local"
                    value={form.availableFrom}
                    onChange={(e) => setForm({ ...form, availableFrom: e.target.value })}
                  />
                  {form.availableFrom && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setForm({ ...form, availableFrom: '' })}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                {fromInvalid && (
                  <p className="mt-1 text-[11px] text-destructive">That date is not valid.</p>
                )}
              </Field>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Leave both empty to release immediately. Days are counted from each learner&apos;s own
              enrollment; if both are set, the later one wins.
              {form.isPreview && (form.availableAfterDays || form.availableFrom)
                ? ' Free-preview lessons are always open, so this schedule is ignored.'
                : ''}
            </p>
          </div>
          <LessonAttachmentsPanel
            lessonId={lesson.id}
            files={lesson.files ?? []}
            onChange={onChange}
          />
          <div className="flex items-center justify-between">
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
              Delete lesson
            </Button>
            <Button
              size="sm"
              onClick={() => save.mutate()}
              disabled={
                save.isPending || !form.title || durationInvalid || daysInvalid || fromInvalid
              }
            >
              {save.isPending ? 'Saving…' : 'Save lesson'}
            </Button>
          </div>
          <ConfirmDialog
            open={pendingSwitch !== null}
            onOpenChange={(o) => !o && setPendingSwitch(null)}
            title={
              pendingSwitch?.next === 'article'
                ? 'Switch to an article?'
                : pendingSwitch?.next === 'r2'
                  ? 'Switch to an uploaded video?'
                  : 'Switch to a video link?'
            }
            description={`${pendingSwitch?.loss ?? ''} Nothing changes until you press Save lesson.`}
            confirmLabel="Switch"
            onConfirm={() => {
              if (pendingSwitch) setForm({ ...form, sourceType: pendingSwitch.next });
              setPendingSwitch(null);
            }}
          />
          <ConfirmDialog
            open={confirmDelete}
            onOpenChange={setConfirmDelete}
            title="Delete this lesson?"
            description="Learner progress on it is lost."
            confirmLabel="Delete"
            confirmVariant="destructive"
            onConfirm={() => remove.mutate(undefined)}
          />
          <ConfirmDialog
            open={guard !== null}
            onOpenChange={(o) => !o && setGuard(null)}
            title={`${guard?.learnerCount ?? 0} learner${guard?.learnerCount === 1 ? '' : 's'} have progress here`}
            description="Their progress, Q&A threads and notes on this lesson are deleted too, and the course's completion total shrinks. This cannot be undone."
            confirmLabel="Delete anyway"
            confirmVariant="destructive"
            onConfirm={() => remove.mutate(true)}
          />
        </div>
      )}
    </div>
  );
}

// ── Cover ───────────────────────────────────────────────────────────────────

function CoverTab({
  course,
  images,
  onChange,
}: {
  course: CourseDetail;
  images: string[];
  onChange: () => void;
}) {
  const upload = useMutation({
    meta: { successMessage: 'Cover uploaded.' },
    mutationFn: (file: File) => uploadCourseCover(course.id, file),
    onSuccess: onChange,
  });
  const remove = useMutation({
    mutationFn: (key: string) => removeCourseCover(course.id, key),
    onSuccess: onChange,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Cover image</CardTitle>
        <p className="text-sm text-muted-foreground">
          16:9 works best. The first image is the card cover in the catalog.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-3">
          {images.map((key, i) => (
            <div key={key} className="relative w-48 overflow-hidden rounded border">
              <img
                src={`${productImageUrl(course.productId, i)}?v=${encodeURIComponent(key)}`}
                alt=""
                className="aspect-video w-full object-cover"
              />
              <Button
                size="sm"
                variant="outline"
                className="absolute right-1 top-1"
                onClick={() => remove.mutate(key)}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
        <Input
          type="file"
          accept="image/png,image/jpeg"
          disabled={upload.isPending}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
          }}
        />
      </CardContent>
      <PromoVideoSection course={course} onChange={onChange} />
    </Card>
  );
}

/**
 * The course's PROMO video — the teaser that plays on the public course page
 * before anyone buys, the way Udemy's does.
 *
 * Lives with the cover because it is the same job: what a stranger sees. It is
 * NOT held under `lms_content_rereview='hold'` — marketing is not the content
 * someone paid for — so what the creator uploads here is live at once.
 */
function PromoVideoSection({ course, onChange }: { course: CourseDetail; onChange: () => void }) {
  const promo = course.promo;
  const { data: me } = useQuery({ queryKey: ['creator-me'], queryFn: getCreatorMe });
  const limits = me?.limits;
  const fileRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [embedUrl, setEmbedUrl] = useState(promo?.embedUrl ?? '');
  const [pct, setPct] = useState<number | null>(null);
  const allowedEmbedHosts = me?.limits?.embedHosts ?? [];

  const upload = useMutation({
    meta: { successMessage: 'Promo video uploaded.', silentErrorCodes: ['video_upload_cancelled'] },
    mutationFn: async (file: File) => {
      if (limits && file.size > limits.maxBytes) {
        throw new Error(
          `That file is ${formatMb(file.size)}; the limit is ${formatMb(limits.maxBytes)}.`,
        );
      }
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setPct(0);
      const resume = await getUploadStatus(promoVideoBase(course.id)).catch(() => undefined);
      return uploadPromoVideo(
        course.id,
        file,
        limits ?? { maxBytes: file.size, partSize: 8 * 1024 * 1024, singlePutMaxBytes: file.size },
        { signal: ctrl.signal, onProgress: (p: number) => setPct(p) },
        resume?.key ? resume : undefined,
      );
    },
    onSettled: () => {
      setPct(null);
      abortRef.current = null;
      if (fileRef.current) fileRef.current.value = '';
    },
    onSuccess: onChange,
  });

  const saveEmbed = useMutation({
    meta: { successMessage: 'Promo video saved.' },
    mutationFn: () => setPromoEmbed(course.id, embedUrl.trim()),
    onSuccess: onChange,
  });

  const remove = useMutation({
    meta: { successMessage: 'Promo video removed.' },
    mutationFn: () => deletePromo(course.id),
    onSuccess: () => {
      setEmbedUrl('');
      onChange();
    },
  });

  const embedPreview = useMemo(() => {
    const url = embedUrl.trim();
    if (!url) return null;
    const embed = resolveRpEmbed(url);
    if (!embed || embed.kind !== 'iframe') return null;
    const provider = detectPlaybackProvider(embed.src);
    if (provider === 'unknown') return null;
    return {
      src: controlSrc(embed.src, provider, {
        origin: typeof window === 'undefined' ? '' : window.location.origin,
        playsinline: true,
      }),
      provider,
    };
  }, [embedUrl]);
  const embedHostProblem =
    embedUrl.trim() &&
    allowedEmbedHosts.length > 0 &&
    !isAllowedEmbedHost(embedUrl.trim(), allowedEmbedHosts)
      ? `That host is not allowed. Use ${allowedEmbedHosts.join(', ')}, or a direct .mp4 link over https.`
      : null;

  return (
    <>
      <CardHeader className="border-t pt-6">
        <CardTitle>Promo video</CardTitle>
        <p className="text-sm text-muted-foreground">
          A short teaser anyone can watch on the course page before buying. Paste a link or upload a
          file — whichever you use replaces the other.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {promo && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge
              variant={
                promo.sourceType === 'r2' && promo.videoStatus !== 'ready' ? 'secondary' : 'default'
              }
            >
              {promo.sourceType === 'embed'
                ? 'Link'
                : promo.videoStatus === 'ready'
                  ? 'Uploaded'
                  : 'Uploading…'}
            </Badge>
            <Button
              size="sm"
              variant="outline"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Remove promo
            </Button>
          </div>
        )}
        <div className="space-y-1">
          <label className="text-sm font-medium">Video link</label>
          <div className="flex gap-2">
            <Input
              value={embedUrl}
              placeholder="https://www.youtube.com/watch?v=…"
              onChange={(e) => setEmbedUrl(e.target.value)}
            />
            <Button
              variant="outline"
              disabled={!embedUrl.trim() || !!embedHostProblem || saveEmbed.isPending}
              onClick={() => saveEmbed.mutate()}
            >
              Save link
            </Button>
          </div>
          {embedHostProblem ? (
            <p className="text-[11px] text-destructive">{embedHostProblem}</p>
          ) : (
            allowedEmbedHosts.length > 0 && (
              <p className="text-[11px] text-muted-foreground">
                Allowed: {allowedEmbedHosts.join(', ')} — or a direct .mp4 link over https.
              </p>
            )
          )}
          {embedPreview && (
            <div className="aspect-video w-full max-w-sm overflow-hidden rounded">
              <VideoPlayer
                source={{ kind: 'iframe', src: embedPreview.src, provider: embedPreview.provider }}
                title="Promo preview"
                protocolId={`promo-${course.id}`}
                chrome="none"
                preferences={{ rate: 1, captionsLang: null }}
              />
            </div>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Or upload a file</label>
          <div className="flex items-center gap-3">
            <Input
              ref={fileRef}
              type="file"
              accept="video/mp4"
              disabled={upload.isPending || !limits}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
              }}
            />
            {upload.isPending && (
              <Button size="sm" variant="outline" onClick={() => abortRef.current?.abort()}>
                Cancel
              </Button>
            )}
          </div>
          {pct != null && <Progress value={pct} />}
          <p className="text-[11px] text-muted-foreground">
            MP4 (H.264/AAC){limits ? `, up to ${formatMb(limits.maxBytes)}` : ''}. Keep it short —
            one to two minutes is what sells a course.
          </p>
        </div>
      </CardContent>
    </>
  );
}

// ── Certificate ─────────────────────────────────────────────────────────────

function CertificateTab({ course, onChange }: { course: CourseDetail; onChange: () => void }) {
  const { data: templates = [] } = useQuery({
    queryKey: ['creator-template-picker'],
    queryFn: listTemplatePicker,
  });
  const assign = useMutation({
    meta: { successMessage: 'Certificate setting saved.' },
    mutationFn: (templateId: string | null) => assignCourseTemplate(course.id, templateId),
    onSuccess: onChange,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Certificate of completion</CardTitle>
        <p className="text-sm text-muted-foreground">
          Learners who finish every lesson — and pass every graded test on the Tests tab, practice
          tests never count — receive this certificate with their name on it.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Select
          value={course.certificateTemplateId ?? 'none'}
          onValueChange={(v) => assign.mutate(v === 'none' ? null : v)}
        >
          <SelectTrigger className="max-w-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No certificate</SelectItem>
            {templates.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
                {t.creatorUserId ? '' : ' (platform)'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Design templates under{' '}
          <Link to="/certificates" className="underline">
            Certificates
          </Link>
          .
        </p>
      </CardContent>
    </Card>
  );
}

// ── Publish ─────────────────────────────────────────────────────────────────

function PublishTab({
  course,
  approvalStatus,
  isHouse,
  onChange,
}: {
  course: CourseDetail;
  approvalStatus?: string;
  isHouse?: boolean;
  onChange: () => void;
}) {
  const lessons = course.chapters.flatMap((c) => c.lessons);
  const lessonCount = lessons.length;
  const { data: tests = [] } = useQuery({
    queryKey: ['creator-course-tests', course.id],
    queryFn: () => listCourseTests(course.id),
  });
  // Only GRADED tests gate the certificate — counting practice ones here would
  // tell the author something the server does not believe.
  const activeTests = tests.filter(
    (t) => !t.isPractice && t.active && t.questions.length > 0,
  ).length;
  const practiceTests = tests.filter((t) => t.isPractice && t.questions.length > 0).length;
  const noContent = lessons.filter((l) =>
    l.sourceType === 'article'
      ? !l.body
      : l.sourceType === 'r2'
        ? l.videoStatus !== 'ready'
        : !l.embedUrl,
  ).length;
  const dripped = lessons.filter(
    (l) => !l.isPreview && ((l.availableAfterDays ?? 0) > 0 || !!l.availableFrom),
  );
  const maxDripDays = dripped.reduce((n, l) => Math.max(n, l.availableAfterDays ?? 0), 0);
  const latestDripDate = dripped.reduce<string | null>(
    (acc, l) => (l.availableFrom && (!acc || l.availableFrom > acc) ? l.availableFrom : acc),
    null,
  );
  const toggle = useMutation({
    meta: {
      successMessage: course.status === 'published' ? 'Course unpublished.' : 'Course published.',
    },
    mutationFn: () =>
      updateCourse(course.id, { status: course.status === 'published' ? 'draft' : 'published' }),
    onSuccess: onChange,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Publish</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <ul className="space-y-1">
          <Check
            ok={lessonCount > 0}
            label={`${lessonCount} lesson${lessonCount === 1 ? '' : 's'} in the curriculum`}
          />
          {!isHouse && (
            <Check
              ok={approvalStatus === 'approved'}
              label={
                approvalStatus === 'approved'
                  ? 'Listing approved by the administrator'
                  : approvalStatus === 'rejected'
                    ? 'Listing rejected — fix the details and save to resubmit'
                    : 'Listing awaiting administrator review'
              }
            />
          )}
          {/* 'clear' is also the state of a course that has never been near
              content review, so a green tick there would claim an approval
              nobody gave — the line only appears when something is open. */}
          {course.contentReviewStatus !== 'clear' && (
            <Check
              ok={false}
              label={
                course.contentReviewStatus === 'pending'
                  ? `Content changes are awaiting review — ${
                      course.contentReviewMode === 'delist'
                        ? 'the listing is paused until an admin reviews it'
                        : course.contentReviewMode === 'hold'
                          ? 'your course stays listed, and your students keep the approved version until an admin approves the change'
                          : 'your course stays listed meanwhile'
                    }`
                  : `Content review rejected: ${
                      course.contentReviewReason ?? 'no reason given'
                    } — fixing the lessons and saving puts the course back in the review queue`
              }
            />
          )}
          <Check
            ok={course.status === 'published'}
            label={course.status === 'published' ? 'Published' : 'Draft (not visible to learners)'}
          />
          {/* Informational, never blocking — a course without tests is fine. */}
          <Check
            ok={activeTests > 0}
            label={`${
              activeTests > 0
                ? `${activeTests} test${activeTests === 1 ? '' : 's'} gate the certificate`
                : 'No test — the certificate is earned by finishing the lessons'
            }${
              practiceTests > 0
                ? ` · ${practiceTests} practice test${practiceTests === 1 ? '' : 's'}, never required`
                : ''
            }`}
          />
          <Check
            ok={noContent === 0}
            label={
              noContent === 0
                ? 'Every lesson has content'
                : `${noContent} lesson${noContent === 1 ? '' : 's'} have no content yet — learners see them locked`
            }
          />
          {dripped.length > 0 && (
            <Check
              ok
              label={`${dripped.length} lesson${
                dripped.length === 1 ? '' : 's'
              } release on a schedule — nobody finishes the course, or earns its certificate, before ${
                maxDripDays > 0 ? `day ${maxDripDays} after enrolling` : ''
              }${maxDripDays > 0 && latestDripDate ? ' and ' : ''}${
                latestDripDate ? new Date(latestDripDate).toLocaleDateString() : ''
              }`}
            />
          )}
        </ul>
        <p className="text-muted-foreground">
          {isHouse
            ? 'The course appears in the catalog once it is published, and becomes sellable at the counter too.'
            : 'The course appears in the catalog only when it is published AND its listing is approved.'}
        </p>
        <Button
          onClick={() => toggle.mutate()}
          disabled={toggle.isPending || (course.status !== 'published' && lessonCount === 0)}
        >
          {course.status === 'published' ? 'Unpublish' : 'Publish'}
        </Button>
      </CardContent>
    </Card>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <span className={ok ? 'text-credit' : 'text-muted-foreground'}>{ok ? '✓' : '○'}</span>
      <span>{label}</span>
    </li>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}

/**
 * datetime-local <input> wants 'YYYY-MM-DDTHH:mm' in LOCAL time. Copied from
 * the admin announcements editor rather than shared — there is no common
 * helper module, and `available_from` is a timestamptz, so a date-only control
 * would silently rewrite an author's 3pm gate to local midnight on every save.
 */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function lines(s: string): string[] {
  return s
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean);
}
